import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { User } from '../../../types';
import { useAuth } from '../../auth/contexts/AuthContext';
import { resolveEffectiveEventStaffing } from '../utils/effectiveEventStaffing';
import { formatTeamTenure } from '../utils/teacherTenure';

const DAYS = [
  ['sun', 'Sun'], ['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'],
  ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'],
] as const;

const SEASON = { key: 'fall2026', label: 'Fall 2026', startMs: new Date('2026-09-01T00:00:00').getTime(), endMs: new Date('2027-01-01T00:00:00').getTime() };

type LeadCommitment = { id: string; title: string; start: Date; end: Date };

function startOfWeek(date: Date): Date {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  next.setDate(next.getDate() - next.getDay());
  return next;
}

function addDays(date: Date, amount: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
}

function sameDay(left: Date, right: Date): boolean {
  return left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth() && left.getDate() === right.getDate();
}

function teacherState(teacher: any): string {
  return String(teacher?.state || teacher?.shippingAddress?.state || '').trim().toUpperCase();
}

function externalUrl(value: string): string {
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

const TeacherTeam: React.FC = () => {
  const { userData } = useAuth();
  const [allTeachers, setAllTeachers] = useState<User[]>([]);
  const [commitmentsByTeacherId, setCommitmentsByTeacherId] = useState<Record<string, LeadCommitment[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [expandedBioIds, setExpandedBioIds] = useState<Set<string>>(() => new Set());
  // null → default to the viewer's own state; '' → all states; 'XX' → that state.
  const [stateFilter, setStateFilter] = useState<string | null>(null);
  const areaState = teacherState(userData);
  const quarterKey = SEASON.key;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getDocs(query(collection(db, 'users'), where('role', '==', 'teacher'))),
      getDocs(collection(db, 'squadEvents')),
    ])
      .then(([snapshot, eventSnapshot]) => {
        if (cancelled) return;
        const next = snapshot.docs
          .map(item => ({ id: item.id, ...(item.data() as any) } as User))
          .filter(teacher => String((teacher as any).staffStatus || 'active').toLowerCase() !== 'offboarded')
          .sort((a, b) => String(a.displayName || '').localeCompare(String(b.displayName || '')));
        setAllTeachers(next);
        const commitments: Record<string, LeadCommitment[]> = {};
        eventSnapshot.docs.forEach(item => {
          const data: any = item.data();
          const teacherId = String(resolveEffectiveEventStaffing(data).effectiveLeadTeacherId || '').trim();
          const start = data.start?.toDate?.() || null;
          const end = data.end?.toDate?.() || null;
          if (!teacherId || !start || !end || start.getTime() < SEASON.startMs || start.getTime() >= SEASON.endMs) return;
          (commitments[teacherId] ||= []).push({ id: item.id, title: String(data.title || 'Class'), start, end });
        });
        Object.values(commitments).forEach(items => items.sort((a, b) => a.start.getTime() - b.start.getTime()));
        setCommitmentsByTeacherId(commitments);
      })
      .catch(fetchError => {
        console.error('Failed to load teacher team', fetchError);
        if (!cancelled) setError('Could not load your team right now.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const availableStates = useMemo(
    () => Array.from(new Set(allTeachers.map(teacherState).filter(Boolean))).sort(),
    [allTeachers]
  );
  const effectiveState = stateFilter === null ? areaState : stateFilter;
  const teachers = useMemo(
    () => allTeachers.filter(teacher => !effectiveState || teacherState(teacher) === effectiveState),
    [allTeachers, effectiveState]
  );

  const areaLabel = useMemo(
    () => (effectiveState ? effectiveState : availableStates.length > 0 ? 'all states' : 'your area'),
    [effectiveState, availableStates]
  );
  const weekDates = useMemo(() => DAYS.map(([, label], index) => ({ label, date: addDays(weekStart, index) })), [weekStart]);
  const weekEnd = weekDates[6].date;
  const weekLabel = `${weekStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${weekEnd.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  const viewingCurrentWeek = weekStart.getTime() === startOfWeek(new Date()).getTime();
  return (
    <section className="teacher-team">
      <div className="teacher-team__header">
        <div><span className="teacher-team__season">🍂 {SEASON.label}</span><h2>Team availability</h2><p>See who is available and who is already committed as a lead teacher in {areaLabel}.</p></div>
        <label className="teacher-team__state-filter">
          <span>State</span>
          <select value={stateFilter ?? areaState} onChange={event => setStateFilter(event.target.value)}>
            <option value="">All states</option>
            {areaState && !availableStates.includes(areaState) && <option value={areaState}>{areaState} (my state)</option>}
            {availableStates.map(state => <option key={state} value={state}>{state === areaState ? `${state} (my state)` : state}</option>)}
          </select>
        </label>
      </div>
      <div className="teacher-team__week-nav">
        <button className="teacher-team__previous" type="button" aria-label="Previous week" onClick={() => setWeekStart(current => addDays(current, -7))}>‹</button>
        <div><strong>{viewingCurrentWeek ? 'This week' : 'Week'}</strong><span>{weekLabel}</span></div>
        {!viewingCurrentWeek && <button className="teacher-team__today" type="button" onClick={() => setWeekStart(startOfWeek(new Date()))}>This week</button>}
        <button className="teacher-team__next" type="button" aria-label="Next week" onClick={() => setWeekStart(current => addDays(current, 7))}>›</button>
      </div>
      <div className="teacher-team__legend"><span><i className="is-available" /> Available</span><span><i className="is-maybe" /> Maybe</span><span><i className="is-committed" /> Already committed as lead</span></div>
      {loading ? <div className="teacher-team__empty">Loading team…</div> : error ? <div className="teacher-team__empty">{error}</div> : teachers.length === 0 ? <div className="teacher-team__empty">No teachers found in {areaLabel}.</div> : (
        <div className="teacher-team__list">
          {teachers.map(teacher => {
            const status = String((teacher as any).staffStatus || 'active').toLowerCase();
            const week: any = (teacher as any).availabilityTemplates?.[quarterKey]?.week;
            const commitments = commitmentsByTeacherId[teacher.id] || [];
            const bio = String(teacher.bio || '').trim();
            const linkedinUrl = String(teacher.linkedinUrl || '').trim();
            const tenure = formatTeamTenure(teacher.startDate);
            const bioExpanded = expandedBioIds.has(teacher.id);
            return <article className="teacher-team__card" key={teacher.id}>
              <div className="teacher-team__identity">
                {teacher.headshotUrl
                  ? <img className="teacher-team__avatar teacher-team__avatar--photo" src={teacher.headshotUrl} alt={`${teacher.displayName || 'Teacher'} profile`} />
                  : <span className="teacher-team__avatar">{String(teacher.displayName || 'T').slice(0, 1).toUpperCase()}</span>}
                <div>
                  <strong>{teacher.displayName || teacher.email || 'Unnamed teacher'}</strong>
                  <span className={`teacher-team__status teacher-team__status--${status}`}>{status}</span>
                  {!effectiveState && teacherState(teacher) && <span className="teacher-team__state-tag">{teacherState(teacher)}</span>}
                  {tenure && <span className="teacher-team__tenure" title={`Team start date: ${teacher.startDate}`}>◷ {tenure}</span>}
                  <div className="teacher-team__profile-actions">
                    {bio && <button className="teacher-team__bio-toggle" type="button" aria-expanded={bioExpanded} aria-controls={`teacher-bio-${teacher.id}`} onClick={() => setExpandedBioIds(current => {
                      const next = new Set(current);
                      if (next.has(teacher.id)) next.delete(teacher.id); else next.add(teacher.id);
                      return next;
                    })}>Bio <span aria-hidden="true">{bioExpanded ? '⌃' : '⌄'}</span></button>}
                    {linkedinUrl && <a className="teacher-team__linkedin" href={externalUrl(linkedinUrl)} target="_blank" rel="noopener noreferrer" aria-label={`Open ${teacher.displayName || 'teacher'}'s LinkedIn profile`}><span aria-hidden="true">in</span> LinkedIn</a>}
                  </div>
                </div>
              </div>
              <div className="teacher-team__availability">
                {DAYS.map(([key], index) => {
                  const { label, date } = weekDates[index];
                  const day = week?.[key];
                  const state = String(day?.status || 'not_set');
                  const blocks = Array.isArray(day?.blocks) ? day.blocks.filter((block: any) => block.status !== 'unavailable') : [];
                  const detail = blocks.length ? blocks.map((block: any) => `${block.start}–${block.end}`).join(', ') : state === 'available' ? 'Available' : state === 'maybe' ? 'Maybe' : state === 'unavailable' ? 'Unavailable' : 'Not set';
                  const dayCommitments = commitments.filter(commitment => sameDay(commitment.start, date));
                  return <div className={`teacher-team__day teacher-team__day--${state}${dayCommitments.length ? ' teacher-team__day--committed' : ''}`} key={key}>
                    <div className="teacher-team__day-heading"><b>{label}</b><time>{date.getDate()}</time></div>
                    <span>{detail}</span>
                    {dayCommitments.map(commitment => <div className="teacher-team__day-commitment" key={commitment.id} title={`${commitment.title} · ${commitment.start.toLocaleString()}`}>
                      <b>Lead · {commitment.start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</b>
                      <span>{commitment.title}</span>
                    </div>)}
                  </div>;
                })}
              </div>
              {bioExpanded && <div className="teacher-team__bio" id={`teacher-bio-${teacher.id}`}><strong>About {String(teacher.displayName || 'this teacher').split(' ')[0]}</strong><p>{bio}</p></div>}
            </article>;
          })}
        </div>
      )}
    </section>
  );
};

export default TeacherTeam;
